/* START OF FILE js/stats.js */
// js/stats.js - Player statistics tracking for Squdle

import StorageManager from './StorageManager.js'; // Import dependency

export default class PlayerStats { // Add export default
  /**
   * Constructor for PlayerStats. Loads stats for the *initial* grid size.
   * @param {StorageManager} storageManager - Instance of StorageManager for persistence
   * @param {number} initialGridSize - The initial grid size (4 or 5)
   */
  constructor(storageManager, initialGridSize = 4) { // Accept initial size
    if (!storageManager) {
        throw new Error('PlayerStats requires a StorageManager instance.');
    }
    this.storageManager = storageManager; // Store reference
    this.currentGridSize = initialGridSize; // Store the size this instance represents initially
    this.data = this.loadStatsForSize(this.currentGridSize); // Load initial stats
    console.log(`PlayerStats initialized for ${this.currentGridSize}x${this.currentGridSize}. Initial Data:`, JSON.stringify(this.data));
  }

  /**
   * Get default stats structure.
   * @returns {Object} Default stats data object.
   * @private
   */
  _getDefaults() {
    return {
      gamesPlayed: 0,
      gamesWon: 0,
      totalSwaps: 0,
      bestScore: Infinity, // Use Infinity for easier comparison
      currentStreak: 0,
      maxStreak: 0,
      hardModeWins: 0,
      playHistory: {}, // dateString -> { completed, swaps, hardMode }
    };
  }

  /**
   * Load stats for a specific grid size using StorageManager.
   * @param {number} gridSize - The grid size (4 or 5).
   * @returns {Object} The loaded or default stats data.
   */
  loadStatsForSize(gridSize) {
    console.log(`[PlayerStats.loadStatsForSize] Attempting to load stats for size: ${gridSize}`); // DEBUG
    let statsData = this._getDefaults(); // Start with defaults
    // console.log(`[PlayerStats.loadStatsForSize] Initial defaults:`, JSON.stringify(statsData)); // DEBUG
    try {
      // Calls StorageManager to get the raw data
      const savedStats = this.storageManager.loadPlayerStats(gridSize);
      // console.log(`[PlayerStats.loadStatsForSize] Data returned from storageManager.loadPlayerStats(${gridSize}):`, savedStats ? JSON.stringify(savedStats) : 'null'); // DEBUG

      // Checks if savedStats is a valid object
      if (savedStats && typeof savedStats === 'object') {
        // console.log(`[PlayerStats.loadStatsForSize] Saved stats found. Merging with defaults...`); // DEBUG
        // Merge saved data with defaults, ensuring all keys exist
        statsData = {
          ...statsData, // Start with defaults
          ...savedStats, // Overwrite with saved data
        };
        // console.log(`[PlayerStats.loadStatsForSize] Data AFTER merge:`, JSON.stringify(statsData)); // DEBUG

        // --- Data Sanitization ---
        // console.log(`[PlayerStats.loadStatsForSize] Starting sanitization...`); // DEBUG
        statsData.gamesPlayed = parseInt(statsData.gamesPlayed) || 0;
        statsData.gamesWon = parseInt(statsData.gamesWon) || 0;
        statsData.totalSwaps = parseInt(statsData.totalSwaps) || 0;
        statsData.bestScore = parseFloat(statsData.bestScore);
        // Ensure bestScore is Infinity if invalid or not present
        if (isNaN(statsData.bestScore) || statsData.bestScore === null) statsData.bestScore = Infinity;
        statsData.currentStreak = parseInt(statsData.currentStreak) || 0;
        statsData.maxStreak = parseInt(statsData.maxStreak) || 0;
        statsData.hardModeWins = parseInt(statsData.hardModeWins) || 0;
        // Ensure playHistory is an object
        if (typeof statsData.playHistory !== 'object' || statsData.playHistory === null) {
          statsData.playHistory = {};
        }
        // Ensure no 'gridSize' property exists in history entries (legacy cleanup)
        Object.values(statsData.playHistory).forEach(entry => delete entry.gridSize);
        // console.log(`[PlayerStats.loadStatsForSize] Finished sanitization.`); // DEBUG
        // console.log(`[PlayerStats.loadStatsForSize] Data AFTER sanitization:`, JSON.stringify(statsData)); // DEBUG

      } else {
        // If no valid savedStats, uses defaults
        // console.log(`[PlayerStats.loadStatsForSize] No valid saved player stats found for ${gridSize}x${gridSize}, using defaults.`); // DEBUG
      }
    } catch (e) {
      // If error during load/parse/sanitize, uses defaults
      console.error(`[PlayerStats.loadStatsForSize] Error loading/processing player stats for ${gridSize}x${gridSize}:`, e);
      statsData = this._getDefaults();
    }

    // Update the instance's current data if we just loaded stats for the current size
    // This might be redundant if called from constructor, but safe otherwise
    if (gridSize === this.currentGridSize) {
        this.data = statsData;
    }

    // Returns the final statsData
    console.log(`[PlayerStats.loadStatsForSize] Returning final statsData for size ${gridSize}:`, JSON.stringify(statsData)); // DEBUG
    return statsData;
  }

  /**
   * Save the current in-memory stats (`this.data`) for the current grid size (`this.currentGridSize`).
   */
  saveStats() {
    // Saves the data currently held in this.data, associated with this.currentGridSize
    this.storageManager.savePlayerStats(this.data, this.currentGridSize);
     // console.log(`[PlayerStats.saveStats] Saved stats for ${this.currentGridSize}x${this.currentGridSize}`);
  }

  /**
   * Record a completed game for a specific grid size.
   * Updates the stats for that size.
   * @param {string} date - Date string YYYY-MM-DD
   * @param {number} swaps - Swaps used
   * @param {number} gridSize - Grid size (4 or 5)
   * @param {boolean} [isHardMode=false] - Whether hard mode was active
   */
  recordCompletion(date, swaps, gridSize, isHardMode = false) {
    // --- START ENHANCED LOGGING ---
    console.log(`[PlayerStats.recordCompletion ENTRY] Args: Date=${date}, Swaps=${swaps}, Size=${gridSize}, HardMode=${isHardMode}`);
    // --- END ENHANCED LOGGING ---

    // Load the stats for the specific grid size being completed
    const targetStats = this.loadStatsForSize(gridSize);
    // --- START ENHANCED LOGGING ---
    console.log(`[PlayerStats.recordCompletion] Loaded targetStats for size ${gridSize} BEFORE modification:`, JSON.stringify(targetStats));
    // --- END ENHANCED LOGGING ---

    // Prevent double counting
    if (targetStats.playHistory[date]?.completed) {
      console.warn(`[PlayerStats.recordCompletion] Stats: Completion for ${date} (${gridSize}x${gridSize}) already recorded. Aborting update.`);
      return; // Exit early
    }

    const numSwaps = parseInt(swaps);
    if (isNaN(numSwaps)) {
      console.error(`[PlayerStats.recordCompletion] Stats: Invalid swap count (${swaps}) for recording completion on ${date}. Aborting update.`);
      return; // Exit early
    }

    // --- Update logic ---
    // Increment games played only if not previously recorded as played/failed today
    if (!targetStats.playHistory[date]) {
      targetStats.gamesPlayed = (targetStats.gamesPlayed || 0) + 1;
    } else {
      // Ensure played count is valid if previously recorded as visit/fail
      targetStats.gamesPlayed = targetStats.gamesPlayed || 1;
    }

    targetStats.gamesWon++; // Increment win count
    targetStats.totalSwaps += numSwaps;

    // Update best score for this size (only if current score is better)
    if (numSwaps < (targetStats.bestScore || Infinity)) {
      targetStats.bestScore = numSwaps;
    }

    if (isHardMode) {
      targetStats.hardModeWins = (targetStats.hardModeWins || 0) + 1; // Ensure it's initialized
    }

    // --- Streak Logic (applied to the specific grid size stats) ---
    const todayObj = new Date(`${date}T12:00:00Z`); // Use UTC noon to avoid timezone issues
    const yesterdayObj = new Date(todayObj);
    yesterdayObj.setUTCDate(todayObj.getUTCDate() - 1);
    const yesterdayString = yesterdayObj.toISOString().split('T')[0];

    // Continue streak if yesterday was completed for this size
    if (targetStats.playHistory[yesterdayString]?.completed) {
      targetStats.currentStreak = (targetStats.currentStreak || 0) + 1;
    } else {
      // Start new streak if yesterday wasn't played or wasn't completed
      targetStats.currentStreak = 1;
    }

    // Update max streak
    if (targetStats.currentStreak > (targetStats.maxStreak || 0)) {
      targetStats.maxStreak = targetStats.currentStreak;
    }

    // Update play history for the specific date - MARK AS COMPLETED
    targetStats.playHistory[date] = {
      completed: true, // *** THIS IS THE KEY UPDATE FOR HISTORY ***
      swaps: numSwaps,
      hardMode: isHardMode,
    };
    // --- End Update logic ---

    // --- START ENHANCED LOGGING ---
    console.log(`[PlayerStats.recordCompletion] Stats modified in memory for size ${gridSize} BEFORE saving:`, JSON.stringify(targetStats));
    // --- END ENHANCED LOGGING ---

    // --- Final Save Call ---
    console.log(`[PlayerStats.recordCompletion] Attempting to save modified stats for size ${gridSize} via storageManager...`);
    const saveAttemptSuccess = this.storageManager.savePlayerStats(targetStats, gridSize); // Pass the modified object
    console.log(`[PlayerStats.recordCompletion] storageManager.savePlayerStats called for size ${gridSize}. Success reported by savePlayerStats: ${saveAttemptSuccess}`);
    // --- END ENHANCED LOGGING ---

    // If the completed size matches the currently active size in this instance, update `this.data`
    if (gridSize === this.currentGridSize) {
      this.data = { ...targetStats }; // Use spread to ensure it's a fresh copy for the instance state
      console.log(`[PlayerStats.recordCompletion] Updated this.data as gridSize matches currentGridSize (${gridSize}).`);
    }

    console.log(
      `[PlayerStats.recordCompletion EXIT] Finished recording completion for ${date} (${gridSize}x${gridSize}). Swaps: ${numSwaps}, HardMode: ${isHardMode}, Streak: ${targetStats.currentStreak}`
    );
  }


  /**
   * Record a game start or visit for a specific grid size.
   * @param {string} date - Date string YYYY-MM-DD
   * @param {number} gridSize - Grid size (4 or 5)
   */
  recordVisit(date, gridSize) {
    const targetStats = this.loadStatsForSize(gridSize);

    // Record visit only if no history exists for this date/size yet
    if (!targetStats.playHistory[date]) {
      console.log(`[PlayerStats.recordVisit] Recording visit for ${date} (${gridSize}x${gridSize})`);
      targetStats.gamesPlayed = (targetStats.gamesPlayed || 0) + 1;

      // Check and potentially break streak for *this specific size*
      // Pass the targetStats object to avoid reloading
      this.checkForStreakBreak(date, gridSize, targetStats);

      // Record the visit (as not completed yet)
      targetStats.playHistory[date] = {
        completed: false,
        swaps: 0, // Swaps are 0 for a visit
        hardMode: false, // Default hard mode to false for a visit record
      };

      // Save the updated stats
      this.storageManager.savePlayerStats(targetStats, gridSize);

      // Update in-memory data if it's the current size
       if (gridSize === this.currentGridSize) {
           this.data = targetStats;
       }
    } else {
        // console.log(`[PlayerStats.recordVisit] Visit for ${date} (${gridSize}x${gridSize}) already recorded. Skipping.`);
    }
  }

  /**
   * Check if the player's streak should be broken for a specific grid size.
   * This should be called *before* potentially updating the streak in recordCompletion or recordVisit.
   * Modifies the passed statsData object directly if streak is broken.
   * @param {string} checkDate - The date for which play is being recorded (e.g., today).
   * @param {number} gridSize - The grid size (4 or 5).
   * @param {Object} statsData - The stats object for the specific grid size (passed by reference).
   * @returns {boolean} - True if streak was broken.
   */
  checkForStreakBreak(checkDate, gridSize, statsData) {
    // Only check if there's a streak to potentially break
    if (statsData.currentStreak > 0) {
      const checkDateObj = new Date(`${checkDate}T12:00:00Z`);
      const yesterdayObj = new Date(checkDateObj);
      yesterdayObj.setUTCDate(checkDateObj.getUTCDate() - 1);
      const yesterdayString = yesterdayObj.toISOString().split('T')[0];

      // If there's no completed record for yesterday for this size, break the streak
      if (!statsData.playHistory[yesterdayString]?.completed) {
        console.log(`[PlayerStats.checkForStreakBreak] Streak broken for ${gridSize}x${gridSize} on ${checkDate} (missed completing ${yesterdayString}).`);
        statsData.currentStreak = 0; // Modify the passed object directly
        // Note: Saving is handled by the calling function (e.g., recordVisit)
        return true; // Streak was broken
      }
    }
    return false; // Streak was not broken
  }

  /**
   * Reset stats for the current grid size in memory.
   * Actual clearing from storage is handled by StorageManager.resetPlayerStats().
   */
  resetStatsInMemory() {
      console.log(`[PlayerStats.resetStatsInMemory] Resetting stats in memory for ${this.currentGridSize}x${this.currentGridSize}.`);
      this.data = this._getDefaults();
      // Note: This doesn't save the reset. StorageManager.resetPlayerStats() should be called for persistent reset.
  }


  /**
   * Get completion rate for the current grid size held in this.data.
   * @returns {number} Completion rate (0-100).
   */
  getCompletionRate() {
    const stats = this.data; // Use current instance data
    return stats.gamesPlayed > 0
      ? Math.round((stats.gamesWon / stats.gamesPlayed) * 100)
      : 0;
  }

  /**
   * Get average swaps per win for the current grid size held in this.data.
   * @returns {number} Average swaps or 0.
   */
  getAverageSwaps() {
    const stats = this.data; // Use current instance data
    return stats.gamesWon > 0
      ? Math.round((stats.totalSwaps / stats.gamesWon) * 10) / 10
      : 0;
  }

}
// Removed window export
/* END OF FILE js/stats.js */
